import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import {
	AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD,
	AI_MENU_SECTION,
	AI_MENU_SECTION_RANK,
	AI_REMIX_MENU_ITEM,
} from '@atlaskit/editor-common/block-menu';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { isRemixInlineDropdownVariant, shouldShowRemixForSelectionContext } from '../remix-config';

const RemixBlockMenuItem = ({ api }: { api: ExtractInjectionAPI<MalleableUiPlugin> }) => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const [anchorRect, setAnchorRect] = useState<DOMRect>();
	const isInlineDropdownVariant = isRemixInlineDropdownVariant();
	// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
	expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);

	// Fire experiment exposure on mount — registration uses expValEqualsNoExposure
	// to avoid over-counting on every editor load. The exposure is recorded here
	// when the Remix button is actually visible to the user.
	useEffect(() => {
		expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true);
	}, []);

	const fireClickAnalytics = useCallback(() => {
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_REMIX_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				entryPoint: 'editor',
				triggeredFrom: 'blockMenu',
			},
		});
	}, [api]);

	const openRemix = useCallback(
		(preselection?: RemixOptionSelectionParameters) => {
			// Close block menu and clear selection highlight
			api?.core?.actions.execute(({ tr }) => {
				api?.blockControls?.commands?.stopPreservingSelection()({ tr });
				api?.blockControls?.commands?.toggleBlockMenu({ closeMenu: true })({ tr });
				api?.selection?.commands?.clearBlockSelection()({ tr });
				return tr;
			});

			// Start experience tracking and open remix modal.
			// malleableUi is guaranteed to exist here — this component is always
			// registered by malleableUiPlugin itself.
			const openRemixModal = api.malleableUi?.commands.openRemixModal({
				metadata: {
					interactionSource: 'remixModalFromBlockMenu',
					...(preselection && {
						preselectedType: preselection.type,
						preselectedSubType: preselection.subType,
					}),
				},
				source: 'remixModalFromBlockMenu',
			});

			if (openRemixModal) {
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: {
						entryPoint: 'editor',
						remixSource: 'blockMenu',
					},
				});
				startRemixCreateExperience({
					experienceTracker,
					attributes: { entryPoint: 'editor', remixSource: 'blockMenu' },
				});
				api.core.actions.execute(openRemixModal);
			}
		},
		[api, experienceTracker],
	);

	const handleClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			fireClickAnalytics();
			if (isInlineDropdownVariant) {
				// The block menu stays open while the user browses the anchored dropdown; it is
				// closed by `openRemix` once an option is committed.
				setAnchorRect(event.currentTarget.getBoundingClientRect());
				return;
			}
			openRemix();
		},
		[fireClickAnalytics, isInlineDropdownVariant, openRemix],
	);

	const closeDropdown = useCallback(() => setAnchorRect(undefined), []);

	const handleOptionSelect = useCallback(
		(selection: RemixOptionSelectionParameters) => {
			closeDropdown();
			openRemix(selection);
		},
		[closeDropdown, openRemix],
	);

	return (
		<>
			<ToolbarDropdownItem
				elemBefore={<AiGenerativeRemixIcon label="" size="small" />}
				elemAfter={isInlineDropdownVariant ? <ChevronRightIcon label="" size="small" /> : undefined}
				onClick={handleClick}
				isSelected={isInlineDropdownVariant ? Boolean(anchorRect) : undefined}
			>
				{expValEquals('cc_maui_create_keyword', 'isEnabled', true)
					? formatMessage(messages.createFromSelection)
					: formatMessage(aiConfigItemMessages.remixTitle)}
			</ToolbarDropdownItem>
			{anchorRect && (
				<RemixContainer
					presentation="popup"
					anchorRect={anchorRect}
					popupPlacement="right-start"
					popupOffset={[0, 0]}
					isThinking={false}
					onClose={closeDropdown}
					onOptionSelect={handleOptionSelect}
				/>
			)}
		</>
	);
};

/**
 * Creates block menu registration components for the Remix action.
 *
 * Registered by editor-plugin-malleable-ui (which owns the openRemixModal command)
 * when the platform_editor_remix_in_block_menu experiment is enabled.
 * The AI plugin hides its polish/improve-formatting action when this experiment is on,
 * so the Remix item effectively replaces it.
 */
const createRemixBlockMenuItemComponent = (api: ExtractInjectionAPI<MalleableUiPlugin>) => {
	const RemixBlockMenuItemWrapper = () => <RemixBlockMenuItem api={api} />;
	RemixBlockMenuItemWrapper.displayName = 'RemixBlockMenuItemWrapper';
	return RemixBlockMenuItemWrapper;
};

/**
 * Per-editor-state cache for the Remix item's `getTextInfo` lookup.
 *
 * The block-menu renderer probes `isHidden` several times during a single render pass
 * (once for the parent section and again for the item), so without memoization the
 * `getTextInfo` doc walk would run repeatedly for the same selection. The cache is keyed
 * by the editor `state` identity, which changes on every transaction, so it invalidates
 * itself automatically.
 */
type RemixTextLengthCache = {
	state: EditorState | null;
	textLength: number | null;
};

const getCachedTextLength = (
	api: ExtractInjectionAPI<MalleableUiPlugin>,
	editorView: EditorView,
	cache: RemixTextLengthCache,
): number | null => {
	const { state } = editorView;
	if (cache.state === state) {
		return cache.textLength;
	}
	const textLength = api?.blockControls?.actions.getTextInfo(editorView)?.textLength ?? null;
	cache.state = state;
	cache.textLength = textLength;
	return textLength;
};

export const getRemixBlockMenuComponents = ({
	api,
	getEditorView,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	getEditorView: () => EditorView | undefined;
}): RegisterBlockMenuComponent[] => {
	const textLengthCache: RemixTextLengthCache = { state: null, textLength: null };

	return [
		{
			type: 'block-menu-item' as const,
			key: `ai-${AI_REMIX_MENU_ITEM.key}`,
			parent: {
				type: 'block-menu-section' as const,
				key: AI_MENU_SECTION.key,
				rank: (AI_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
			},
			component: createRemixBlockMenuItemComponent(api),
			isHidden: () => {
				if (
					!expValEqualsNoExposure('platform_editor_ai_blockmenu_integration', 'isEnabled', true)
				) {
					return true;
				}
				const editorView = getEditorView();
				const menuTriggerByNode =
					api?.blockControls?.sharedState?.currentState()?.menuTriggerByNode;
				const triggerPos = menuTriggerByNode?.rootPos ?? menuTriggerByNode?.pos;
				const triggeredNode =
					editorView && triggerPos !== undefined
						? editorView.state.doc.nodeAt(triggerPos)
						: undefined;

				if (
					triggeredNode?.type.name === 'extension' &&
					shouldShowRemixForSelectionContext({ node: triggeredNode }) &&
					fg('maui_remixable_page_native_embeds')
				) {
					return false;
				}

				const textLength = editorView
					? getCachedTextLength(api, editorView, textLengthCache)
					: null;
				// When context is unavailable, default to visible (matches AI plugin behaviour)
				if (textLength === null) {
					return false;
				}
				return textLength < AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD;
			},
		},
	];
};
